import { ErrorEventsEnum } from '@northladder/utilities';
import { logErrorToSentry } from '@northladder/web-utils';

/**
 * Waits for specified milliseconds and returns timeout ID for cleanup
 */
export function wait(ms: number): {
  promise: Promise<void>;
  timeoutId: ReturnType<typeof setTimeout>;
} {
  let timeoutId: ReturnType<typeof setTimeout>;

  const promise = new Promise<void>((resolve) => {
    timeoutId = setTimeout(() => {
      resolve();
    }, ms);
  });

  return { promise, timeoutId: timeoutId! };
}

/**
 * Ensures audio is loaded and ready for playback
 */
export function waitForAudioReady(
  audio: HTMLAudioElement,
  errorMessage: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const audioElement = audio;

    if (audioElement.readyState >= 3) {
      resolve();

      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let isResolved = false;
    let onCanPlayThroughHandler: (() => void) | null = null;
    let onErrorHandler: (() => void) | null = null;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const removeAllListeners = () => {
      if (onCanPlayThroughHandler) {
        audioElement.removeEventListener(
          'canplaythrough',
          onCanPlayThroughHandler
        );
      }

      if (onErrorHandler) {
        audioElement.removeEventListener('error', onErrorHandler);
      }
    };

    onErrorHandler = () => {
      if (isResolved) return;

      isResolved = true;
      cleanup();
      removeAllListeners();

      const technicalError = new Error(
        `Audio failed to load. readyState: ${audioElement.readyState}, networkState: ${audioElement.networkState}`
      );
      logErrorToSentry({
        error: technicalError,
        errorKey: ErrorEventsEnum.ERROR_IN_PERMISSION,
        errorName: 'AUDIO_LOAD_FAILED',
        source: 'audioPlaybackUtils.waitForAudioReady',
      });
      reject(new Error(errorMessage));
    };

    onCanPlayThroughHandler = () => {
      if (isResolved) return;

      isResolved = true;
      cleanup();
      removeAllListeners();
      resolve();
    };

    audioElement.addEventListener('canplaythrough', onCanPlayThroughHandler);
    audioElement.addEventListener('error', onErrorHandler);

    timeoutId = setTimeout(() => {
      if (isResolved) return;

      isResolved = true;
      removeAllListeners();

      if (audioElement.readyState >= 2) {
        resolve();
      } else {
        const technicalError = new Error(
          `Audio loading timeout. readyState: ${audioElement.readyState}, networkState: ${audioElement.networkState}`
        );
        logErrorToSentry({
          error: technicalError,
          errorKey: ErrorEventsEnum.ERROR_IN_PERMISSION,
          errorName: 'AUDIO_LOAD_TIMEOUT',
          source: 'audioPlaybackUtils.waitForAudioReady',
        });
        reject(new Error(errorMessage));
      }
    }, 5000);

    if (audioElement.readyState >= 3) {
      cleanup();
      removeAllListeners();
      resolve();
    }
  });
}

/**
 * Plays audio and waits for it to finish
 * Fails the test if playback doesn't complete within a reasonable time
 * Includes retry logic for failed playback attempts
 */
export function playAudioUntilEnded(
  audio: HTMLAudioElement,
  maxRetries: number,
  errorMessage: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const audioElement = audio;
    let retryCount = 0;

    const attemptPlayback = (): void => {
      waitForAudioReady(audioElement, errorMessage)
        .then(() => {
          let timeoutId: ReturnType<typeof setTimeout> | null = null;
          let playbackStartTimeoutId: ReturnType<typeof setTimeout> | null =
            null;
          let isResolved = false;

          const cleanup = () => {
            if (timeoutId) {
              clearTimeout(timeoutId);
              timeoutId = null;
            }

            if (playbackStartTimeoutId) {
              clearTimeout(playbackStartTimeoutId);
              playbackStartTimeoutId = null;
            }
          };

          const resolveOnce = () => {
            if (isResolved) return;

            isResolved = true;
            cleanup();

            if (!audioElement.ended) {
              const technicalError = new Error(
                'Audio playback did not complete - audio ended event fired but audio.ended is false'
              );
              logErrorToSentry({
                error: technicalError,
                errorKey: ErrorEventsEnum.ERROR_IN_PERMISSION,
                errorName: 'AUDIO_PLAYBACK_INCOMPLETE',
                source: 'audioPlaybackUtils.playAudioUntilEnded',
              });
              reject(new Error(errorMessage));

              return;
            }

            resolve();
          };

          const rejectOnce = (technicalError: Error, userMessage?: string) => {
            if (isResolved) return;

            isResolved = true;
            cleanup();

            if (retryCount < maxRetries) {
              retryCount += 1;
              audioElement.load();
              setTimeout(() => {
                attemptPlayback();
              }, 500);

              return;
            }

            logErrorToSentry({
              error: technicalError,
              errorKey: ErrorEventsEnum.ERROR_IN_PERMISSION,
              errorName: 'AUDIO_PLAYBACK_ERROR',
              source: 'audioPlaybackUtils.playAudioUntilEnded',
            });
            reject(new Error(userMessage || errorMessage));
          };

          audioElement.onended = () => {
            resolveOnce();
          };

          audioElement.onerror = () => {
            const { error } = audioElement;
            const technicalError = new Error(
              error
                ? `Audio playback error: code=${error.code}, message=${error.message}`
                : 'Audio playback error'
            );
            rejectOnce(technicalError, errorMessage);
          };

          const playPromise = audioElement.play();

          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                playbackStartTimeoutId = setTimeout(() => {
                  if (audioElement.paused || audioElement.readyState < 2) {
                    const technicalError = new Error(
                      `Audio playback did not start. Paused: ${audioElement.paused}, readyState: ${audioElement.readyState}`
                    );
                    rejectOnce(technicalError, errorMessage);
                  }
                }, 3000);

                const audioDuration = audioElement.duration;
                const maxTimeout =
                  audioDuration && Number.isFinite(audioDuration)
                    ? audioDuration * 1000 + 10000
                    : 30000;

                timeoutId = setTimeout(() => {
                  if (!audioElement.ended) {
                    const technicalError = new Error(
                      `Audio playback did not complete within ${maxTimeout}ms. Audio ended: ${audioElement.ended}, currentTime: ${audioElement.currentTime}, duration: ${audioElement.duration}`
                    );
                    rejectOnce(technicalError, errorMessage);
                  }
                }, maxTimeout);
              })
              .catch((error) => {
                const technicalError =
                  error instanceof Error ? error : new Error(String(error));

                if (
                  retryCount < maxRetries &&
                  (technicalError.name === 'NotAllowedError' ||
                    technicalError.name === 'NotSupportedError')
                ) {
                  retryCount += 1;
                  audioElement.load();
                  setTimeout(() => {
                    attemptPlayback();
                  }, 1000);

                  return;
                }

                rejectOnce(technicalError, errorMessage);
              });
          } else {
            const technicalError = new Error('Audio play() returned undefined');
            rejectOnce(technicalError, errorMessage);
          }
        })
        .catch((error) => {
          if (retryCount < maxRetries) {
            retryCount += 1;
            audioElement.load();
            setTimeout(() => {
              attemptPlayback();
            }, 500);

            return;
          }

          const technicalError =
            error instanceof Error ? error : new Error(String(error));
          logErrorToSentry({
            error: technicalError,
            errorKey: ErrorEventsEnum.ERROR_IN_PERMISSION,
            errorName: 'AUDIO_LOAD_FAILED',
            source: 'audioPlaybackUtils.playAudioUntilEnded',
          });
          reject(new Error(errorMessage));
        });
    };

    attemptPlayback();
  });
}

/**
 * Sets up audio element with Web Audio API gain node for playback
 */
export async function setupAudioWithWebAudio(
  audio: HTMLAudioElement,
  audioContext: AudioContext,
  gainValue = 2.0
): Promise<void> {
  if (audioContext.state === 'suspended') {
    await audioContext.resume();
  }

  const source = audioContext.createMediaElementSource(audio);
  const gainNode = audioContext.createGain();

  gainNode.gain.value = gainValue;
  source.connect(gainNode);
  gainNode.connect(audioContext.destination);
}

/**
 * Configures audio element for mobile devices
 */
export function configureAudioForMobile(audio: HTMLAudioElement): void {
  audio.setAttribute('playsinline', 'true');
  audio.setAttribute('preload', 'auto');
  audio.setAttribute('crossorigin', 'anonymous');
}

/**
 * Waits for audio element to be ready for playback
 */
export function waitForAudioCanPlay(audio: HTMLAudioElement): Promise<void> {
  return new Promise<void>((resolve) => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let isResolved = false;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const resolveOnce = () => {
      if (isResolved) return;

      isResolved = true;
      cleanup();
      resolve();
    };

    const checkReady = () => {
      if (isResolved) return;

      if (audio.readyState >= 2) {
        resolveOnce();

        return;
      }

      timeoutId = setTimeout(() => {
        checkReady();
      }, 50);
    };

    audio.addEventListener(
      'canplay',
      () => {
        resolveOnce();
      },
      { once: true }
    );

    checkReady();
  });
}
